<template>
	<QuestionGroup>
		<!-- Question 1: Select schedule type -->
		<QuestionCard
			title="When would you like the scan to run?"
			description="Choose when the scan should start, then configure any required scheduling options."
		>
			<validation-provider
				#default="{ errors }"
				name="Scan Schedule"
				:rules="{ required: siteScan.scanSchedule === null }"
				:value="siteScan.scanSchedule"
			>
				<div class="d-flex flex-row flex-wrap schedule-card-row">
					<SelectorCard
						v-for="card in scheduleCards"
						:key="card.value"
						:title="card.title"
						:description="card.description"
						:model-value="siteScan.scanSchedule === card.value"
						:disabled="!siteScannerFullAccess"
						class="schedule-card"
						@input="selectSchedule(card.value)"
					>
						<template #icon>
							<v-icon
								color="primary"
								size="24"
							>
								{{ card.icon }}
							</v-icon>
						</template>
					</SelectorCard>
				</div>
				<div
					v-if="errors[0]"
					class="error--text text-caption mt-2"
				>
					{{ errors[0] }}
				</div>
			</validation-provider>
			<div
				v-if="isScanNow"
				class="mt-4 text-caption"
			>
				The scan will be requested immediately.
			</div>
		</QuestionCard>

		<!-- Dynamic / Progressive Questions with Transition -->
		<transition-group
			name="fade-expand"
			tag="div"
			class="schedule-dynamic-group"
		>
			<!-- Single Scan: Date -->
			<QuestionCard
				v-if="isSingleScan"
				key="single-date"
				title="What date should the scan run?"
				description="Pick a date for a one-off scan"
			>
				<v-row dense>
					<v-col cols="4">
						<DatePicker
							:date="scanStartDateFormatted"
							:min="today"
							:disabled="!siteScannerFullAccess"
							rules="required"
							label="Scan Date"
							class="cassie-input-width-sm mr-4"
							@update:date="onUpdateScanStartDate($event)"
						/>
					</v-col>
				</v-row>
			</QuestionCard>
			<!-- Single Scan: Time -->
			<QuestionCard
				v-if="isSingleScan"
				key="single-time"
				title="What time should the scan run?"
				description="Enter the time in 24 hour clock format hh:mm."
			>
				<v-row dense>
					<v-col cols="4">
						<TextField
							:value="siteScan.scanRunTime"
							label="Scan Run Time *"
							:rules="{required:true, regex: time24regex}"
							:disabled="!siteScannerFullAccess"
							@input="updateScanRequest('scanRunTime', $event)"
						/>
					</v-col>
				</v-row>
			</QuestionCard>
			<!-- Single Scan: Summary -->
			<div
				v-if="isSingleScan"
				key="single-summary"
				class="text-caption"
			>
				<div class="mt-4">
					Based on your selections, this scan will be scheduled for {{ scanDateFormatted }} {{ serverTimezone }}
				</div>
				<div class="mt-2">
					It is currently {{ currentServerTimeFormatted }} in {{ serverTimezone }}
				</div>
			</div>

			<!-- Recurring: Frequency -->
			<QuestionCard
				v-if="isRecurringScan"
				key="recurring-frequency"
				title="How often should the scan repeat?"
				description="Choose a recurrence frequency."
			>
				<div class="d-flex flex-row flex-wrap schedule-card-row">
					<SelectorCard
						v-for="card in frequencyCards"
						:key="card.value"
						:title="card.title"
						:description="card.description"
						:model-value="siteScan.scanFrequency === card.value"
						:disabled="!siteScannerFullAccess"
						class="schedule-card"
						@input="selectFrequency(card.value)"
					>
						<template #icon>
							<v-icon
								color="primary"
								size="24"
							>
								{{ card.icon }}
							</v-icon>
						</template>
					</SelectorCard>
				</div>
			</QuestionCard>
			<!-- Recurring: Day Selection -->
			<QuestionCard
				v-if="showRecurringDayCard"
				key="recurring-day"
				:title="daySelectionTitle"
				:description="daySelectionDescription"
			>
				<v-row dense>
					<v-col
						v-if="weeklyScanFrequency"
						cols="4"
					>
						<Dropdown
							:value="siteScan.scanDayOfWeek"
							label="Day of Week *"
							:items="dayItems"
							rules="required"
							custom-sort
							:disabled="!siteScannerFullAccess"
							@change="updateScanRequest('scanDayOfWeek', $event)"
						/>
					</v-col>
					<v-col
						v-if="monthlyScanFrequency || quarterlyScanFrequency"
						cols="4"
					>
						<Dropdown
							:value="siteScan.scanDayOfMonth"
							label="Day of Month *"
							:items="dayOfMonthItems"
							rules="required"
							custom-sort
							:disabled="!siteScannerFullAccess"
							@change="updateScanRequest('scanDayOfMonth', $event)"
						/>
					</v-col>
				</v-row>
			</QuestionCard>
			<!-- Recurring: Time -->
			<QuestionCard
				v-if="showRecurringTimeCard"
				key="recurring-time"
				title="What time should each scan run?"
				description="Enter the time in 24 hour clock format hh:mm."
			>
				<v-row dense>
					<v-col cols="4">
						<TextField
							:value="siteScan.scanRunTime"
							label="Scan Run Time *"
							:rules="{required:true, regex: time24regex}"
							placeholder="00:01"
							:disabled="!siteScannerFullAccess"
							@input="updateScanRequest('scanRunTime', $event)"
						/>
					</v-col>
				</v-row>
			</QuestionCard>
			<!-- Recurring: Start / Exclusions -->
			<QuestionCard
				v-if="showRecurringStartCard"
				key="recurring-start"
				title="When can recurring scans start?"
				description="Set the earliest start date and optional exclusion date."
			>
				<v-row dense>
					<v-col cols="4">
						<DatePicker
							:date="scanStartDateFormatted"
							:min="today"
							:disabled="!siteScannerFullAccess"
							label="Don't Run Before"
							class="cassie-input-width-sm"
							rules="required"
							@update:date="onUpdateScanStartDate($event)"
						/>
					</v-col>
					<v-col
						v-if="siteScan.scanType !== singleUrlScanType"
						cols="4"
					>
						<DatePicker
							:date="scanExcludeBeforeDateFormatted"
							:max="today"
							:disabled="!siteScannerFullAccess"
							rules="required"
							label="Exclude Before Date"
							@update:date="onUpdateScanExcludeBeforeDate($event)"
						/>
					</v-col>
				</v-row>
				<div class="mt-4 text-caption">
					Based on your selections, your first scan will be scheduled for {{ firstScanDateDisplay }}
				</div>
				<div class="mt-2 text-caption text--secondary">
					It is currently {{ currentServerTimeFormatted }} in {{ serverTimezone }}
				</div>
			</QuestionCard>
		</transition-group>
	</QuestionGroup>
</template>
<script>
import { format } from 'date-fns'
import { ValidationProvider } from 'vee-validate'
import DatePicker from '../../../../../../shared/components/date-picker.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import { QuestionCard, QuestionGroup, SelectorCard } from '../../../../components/component-library-wrapper.js'
import { DATE_FORMAT_DATE_PICKER } from '../../../../../../shared/utils/date-formatting.js'

export default {
	name: 'scan-schedule-step',
	components: { QuestionCard, QuestionGroup, SelectorCard, DatePicker, TextField, Dropdown, ValidationProvider },
	props: {
		siteScan: { type: Object, required: true },
		scanScheduleItems: { type: Array, default: () => [] },
		scanFrequencyItems: { type: Array, default: () => [] },
		dayItems: { type: Array, default: () => [] },
		dayOfMonthItems: { type: Array, default: () => [] },
		scheduleSingleScanValue: { type: Number, required: true },
		scheduleRecurringScanValue: { type: Number, required: true },
		scanDateFormatted: { type: String, default: '' },
		firstScanDateDisplay: { type: String, default: '' },
		currentServerTimeFormatted: { type: String, default: '' },
		serverTimezone: { type: String, default: '' },
		today: { type: String, default: '' },
		siteScannerFullAccess: { type: Boolean, default: false },
		time24regex: { type: [RegExp, Object], required: true },
		updateScanRequest: { type: Function, required: true },
		singleUrlScanType: { type: Number, required: true }
	},
	computed: {
		scheduleCards () {
			// Provide default descriptions/icons for each schedule type value (1=Now,2=Single,3=Recurring)
			return this.scanScheduleItems.map(item => {
				let description = ''
				let icon = 'mdi-clock-outline'
				switch (item.value) {
					case 1:
						description = 'Start as soon as possible'
						icon = 'mdi-flash'
						break
					case this.scheduleSingleScanValue:
						description = 'Run once at a chosen date/time'
						icon = 'mdi-calendar-clock'
						break
					case this.scheduleRecurringScanValue:
						description = 'Run on a repeating schedule'
						icon = 'mdi-repeat'
						break
				}
				return { ...item, title: item.text, description, icon }
			})
		},
		isScanNow () { return this.siteScan.scanSchedule === 1 },
		isSingleScan () { return this.siteScan.scanSchedule === this.scheduleSingleScanValue },
		isRecurringScan () { return this.siteScan.scanSchedule === this.scheduleRecurringScanValue },
		weeklyScanFrequency () { return this.siteScan.scanFrequency === 2 },
		monthlyScanFrequency () { return this.siteScan.scanFrequency === 3 },
		quarterlyScanFrequency () { return this.siteScan.scanFrequency === 4 },
		timeIsValid () {
			try {
				const regex = this.time24regex instanceof RegExp ? this.time24regex : new RegExp(this.time24regex)
				return regex.test(this.siteScan.scanRunTime || '')
			} catch (e) { return false }
		},
		frequencyCards () {
			// Map frequency items to selector cards with icons / descriptions
			return this.scanFrequencyItems.map(item => {
				let description = ''
				let icon = 'mdi-calendar'
				switch (item.value) {
					case 2:
						description = 'Run every week on a chosen day'
						icon = 'mdi-calendar-week'
						break
					case 3:
						description = 'Run once per month'
						icon = 'mdi-calendar-month'
						break
					case 4:
						description = 'Run once per quarter'
						icon = 'mdi-calendar-range'
						break
				}
				return { ...item, title: item.text, description, icon }
			})
		},
		daySelectionTitle () {
			if (this.weeklyScanFrequency) return 'Which day of the week?'
			if (this.monthlyScanFrequency) return 'Which day of the month?'
			if (this.quarterlyScanFrequency) return 'Which day of the quarter?'
			return 'Select day'
		},
		daySelectionDescription () {
			if (this.weeklyScanFrequency) return 'Choose the weekday for the weekly scan.'
			if (this.monthlyScanFrequency) return 'Choose the day of the month (1-28) when the scan should run.'
			if (this.quarterlyScanFrequency) return 'Choose the day (1-28) used for each quarterly run.'
			return ''
		},
		scanStartDateFormatted () {
			const d = this.normalizeDate(this.siteScan.scanStartDate)
			if (!d) return ''
			return format(d, DATE_FORMAT_DATE_PICKER)
		},
		scanExcludeBeforeDateFormatted () {
			const d = this.normalizeDate(this.siteScan.scanExcludeBeforeDate)
			if (!d) return ''
			return format(d, DATE_FORMAT_DATE_PICKER)
		},
		showRecurringDayCard () {
			// Show day selection as soon as a frequency is chosen (>=2) and recurring is active
			return this.isRecurringScan && [2, 3, 4].includes(this.siteScan.scanFrequency)
		},
		showRecurringTimeCard () {
			// Need day selection satisfied (or not required for weekly vs monthly/quarterly) before time
			if (!this.showRecurringDayCard) return false
			if (this.weeklyScanFrequency && !this.siteScan.scanDayOfWeek) return false
			if ((this.monthlyScanFrequency || this.quarterlyScanFrequency) && !this.siteScan.scanDayOfMonth) return false
			return true
		},
		showRecurringStartCard () {
			// Only after a valid time has been entered
			return this.showRecurringTimeCard && this.timeIsValid
		}
	},
	methods: {
		normalizeDate (value) {
			if (!value) return null
			// If already a Date and valid
			if (value instanceof Date) {
				return isNaN(value.getTime()) ? null : value
			}
			// If it's a string (expected from DatePicker) try to construct
			try {
				const d = new Date(value)
				return isNaN(d.getTime()) ? null : d
			} catch (e) { return null }
		},
		selectSchedule (value) {
			if (!this.siteScannerFullAccess) return
			this.updateScanRequest('scanSchedule', value)
			// If switching away from recurring, reset frequency & clear any previously selected day values
			if (value !== this.scheduleRecurringScanValue) {
				this.updateScanRequest('scanFrequency', 1)
				if (this.siteScan.scanDayOfWeek) this.updateScanRequest('scanDayOfWeek', null)
				if (this.siteScan.scanDayOfMonth) this.updateScanRequest('scanDayOfMonth', null)
			} else if (value === this.scheduleRecurringScanValue && this.siteScan.scanFrequency === 1) {
				// Start recurring with weekly frequency but DO NOT auto-select a day; user must choose
				this.updateScanRequest('scanFrequency', 2)
				if (this.siteScan.scanDayOfWeek) this.updateScanRequest('scanDayOfWeek', null)
				if (this.siteScan.scanDayOfMonth) this.updateScanRequest('scanDayOfMonth', null)
			}
		},
		selectFrequency (value) {
			if (!this.siteScannerFullAccess) return
			this.updateScanRequest('scanFrequency', value)
			// Clear opposing day fields when switching frequency; don't auto-assign defaults
			if (value === 2) {
				// Weekly: clear any month/quarter day selection; keep dayOfWeek for user to choose
				if (this.siteScan.scanDayOfMonth) this.updateScanRequest('scanDayOfMonth', null)
				if (this.siteScan.scanDayOfWeek) this.updateScanRequest('scanDayOfWeek', null) // force explicit user choice
			}
			if (value === 3 || value === 4) {
				// Monthly/Quarterly: clear weekday and month day to require explicit selection
				if (this.siteScan.scanDayOfWeek) this.updateScanRequest('scanDayOfWeek', null)
				if (this.siteScan.scanDayOfMonth) this.updateScanRequest('scanDayOfMonth', null)
			}
		},
		onUpdateScanStartDate (date) {
			const normalized = this.normalizeDate(date)
			if (normalized) this.updateScanRequest('scanStartDate', normalized)
		},
		onUpdateScanExcludeBeforeDate (date) {
			const normalized = this.normalizeDate(date)
			if (normalized) this.updateScanRequest('scanExcludeBeforeDate', normalized)
		}
	}
}
</script>
<style scoped>
.schedule-card-row {
  gap: 12px;
}
.schedule-card {
  flex: 1 1 220px;
  min-width: 200px;
  max-width: 260px;
}
.schedule-dynamic-group {
	display: flex;
	flex-direction: column;
	gap: 20px;
	margin-top: 8px;
}

/* Transition (mirrors scan-type-step) */
.fade-expand-enter-active,
.fade-expand-leave-active { transition: opacity .24s ease, transform .24s ease; }
.fade-expand-enter,
.fade-expand-leave-to { opacity:0; transform:translateY(-6px); }
.fade-expand-enter-to,
.fade-expand-leave { opacity:1; transform:translateY(0); }
</style>
